import createReducer from "./createReducer";
import { addFiltering } from "./common/Filtering";
import { addColumnFilters } from "./common/ColumnFilters";
import { CREATIVE_VERSIONS_ACTIONS } from "./actions/CreativeVersions";
import { LABELS_ACTIONS } from "./Labels";

import { SUPPRESS_SUCCESS_GROWL, SUPPRESS_ALL_GROWL, VERSION_STATUS } from "utils/constants";
import { escapeUnderscoreForAPI } from "utils/common";

export function clearCreativeVersions(){
	return (dispatch) => {
		dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.RESET_CREATIVE_VERSIONS_LIST,
		});

		return Promise.resolve({ success: true });
	};
}

export function loadCreativeVersions(libraryID, creativeID, cancelToken){
	return (dispatch, getState) => {
		let state = getState();
		let { filterInfo = {} } = state.creativeVersions;
		const params = { ...filterInfo };

		if (params.searchTerm) {
			params.searchTerm = escapeUnderscoreForAPI(filterInfo.searchTerm);
		}

		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryID}/creatives/${creativeID}/versions`,
				params,
				success: CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_ERROR,
				cancel: CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_CANCEL,
				cancelToken,
			},
		});
	};
}

export function fetchAllVersions(libraryId, creativeId){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.FETCH_ALL_CREATIVE_VERSIONS_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/versions`,
				params: {
					rpp: -1, // No limit, return everything
				},
				success: CREATIVE_VERSIONS_ACTIONS.FETCH_ALL_CREATIVE_VERSIONS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.FETCH_ALL_CREATIVE_VERSIONS_ERROR,
			},
		});
	};
}

export function fetchSingleVersion(libraryId, creativeId, versionId){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.FETCH_SINGLE_CREATIVE_VERSION_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/versions/${versionId}`,
				success: CREATIVE_VERSIONS_ACTIONS.FETCH_SINGLE_CREATIVE_VERSION_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.FETCH_SINGLE_CREATIVE_VERSION_ERROR,
			},
		}).then((response) => {
			if(response?.success){
				let item = response.payload?.data;
				return Promise.resolve(item);
			}
		});
	};
}

export function fetchNamedVersion(libraryId, creativeId, versionName){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.FETCH_NAMED_CREATIVE_VERSION_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/versions`,
				params: {
					advancedFilter: `eq(name,${versionName})`,
				},
				success: CREATIVE_VERSIONS_ACTIONS.FETCH_NAMED_CREATIVE_VERSION_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.FETCH_NAMED_CREATIVE_VERSION_ERROR,
			},
		}).then((response) => {
			if(response?.success){
				let item = response.payload?.data?.items?.[0];
				return Promise.resolve(item);
			}
		});
	};
}

export function deleteVersions({ libraryId, creativeId, versionIds }){
	return (dispatch) => {
		return dispatch({
			type:CREATIVE_VERSIONS_ACTIONS.DELETE_VERSIONS_AJAX,
			AJAX:{
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/instantads`,
				data: {
					versionIds,
				},
				method:"delete",
				success:CREATIVE_VERSIONS_ACTIONS.DELETE_VERSIONS_SUCCESS,
				error:CREATIVE_VERSIONS_ACTIONS.DELETE_VERSIONS_ERROR,
			},
		});
	};
}

export function createVersion(libraryId, creativeId, versionData, meta = {}){
	let newData = {
		...versionData,
		// Newly created versions should start out unpublished, without any weighting.
		status: VERSION_STATUS.UNPUBLISHED,
		weighting: 0,
		weightMultiplier: 0,
	};

	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.CREATE_CREATIVE_VERSION_AJAX,
			meta,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/versions`,
				method: 'post',
				data: newData,
				success: CREATIVE_VERSIONS_ACTIONS.CREATE_CREATIVE_VERSION_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.CREATE_CREATIVE_VERSION_ERROR,
			},
		});
	};
}

export function updateVersion(libraryId, creativeId, versionId, versionData){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.UPDATE_VERSION_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/versions/${versionId}`,
				method: 'put',
				data: versionData,
				success: CREATIVE_VERSIONS_ACTIONS.UPDATE_VERSION_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.UPDATE_VERSION_ERROR,
			},
		});
	};
}

export function duplicateVersion({ creativeId, libraryId, data, newName }){
	let newData = {
		...data,
		name: newName,
	};

	return createVersion(libraryId, creativeId, newData);
}

function multiVariantBuildMessage(id){
	return function buildMessage(response, success){
		if(success){
			return {
				type: "success",
				id,
				messageHeader: "Success!",
				message: "Multi variant creative successfully saved",
			};
		}

		return {
			type: "error",
			id,
			messageHeader: "Error!",
			message: "Error saving multi variant creative",
		};
	};
}

export function createAutoOptimizedCreative(creativeId, autoOptimizationGoal, versionList, advertiserObjective){
	// versionList is expected to be an array of objects in the form: { id }
	let data;
	if(advertiserObjective) {
		//modern enabled
		data = {
			advertiserObjective: advertiserObjective.value,
			isAutoOptimization: true,
			autoOptimizationGoal,
			versionList,
		};
	} else {
		data = {
			isAutoOptimization: true,
			autoOptimizationGoal,
			versionList,
		};
	}

	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.CREATE_AUTO_OPTIMIZED_CREATIVE_AJAX,
			AJAX: {
				url:`ui/creatives/${creativeId}/mvo`,
				gateway:"dt",
				data,
				method: "post",
				success: CREATIVE_VERSIONS_ACTIONS.CREATE_AUTO_OPTIMIZED_CREATIVE_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.CREATE_AUTO_OPTIMIZED_CREATIVE_ERROR,
			},
			meta: {
				buildMessage: multiVariantBuildMessage((new Date()).getTime()),
			},
		});
	};
}

export function createWeightedRotationCreative(libraryId, creativeId, weightedVersions = []){
	/*
		weightedVersions is expected to be an array of objects in the form:
		{
			id,
			weightMultiplier,
		}
	*/
	const url = `creative-libraries/${libraryId}/decision-trees/creatives/${creativeId}/weights-multiplier`;
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.CREATE_WEIGHTED_ROTATION_CREATIVE_AJAX,
			AJAX: {
				url,
				data: {
					autoOptimize: false,
					instantAdList: weightedVersions,
				},
				method: "post",
				success: CREATIVE_VERSIONS_ACTIONS.CREATE_WEIGHTED_ROTATION_CREATIVE_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.CREATE_WEIGHTED_ROTATION_CREATIVE_ERROR,
			},
			meta: {
				buildMessage: multiVariantBuildMessage(url),
			},
		});
	};
}

export function publishVersion(libraryId, creativeId, versionId){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.PUBLISH_VERSION_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/decision-trees/creatives/${creativeId}/instant-ads/${versionId}/publish`,
				method: "post",
				success: CREATIVE_VERSIONS_ACTIONS.PUBLISH_VERSION_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.PUBLISH_VERSION_ERROR,
			},
		});
	};
}

export function socialPublishVersion(versionIds) {
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.SOCIAL_PUBLISH_VERSION_AJAX,
			payload: { isPublish: true },
			AJAX: {
				url: "ui/versions/social/publish",
				method: "post",
				success: CREATIVE_VERSIONS_ACTIONS.SOCIAL_PUBLISH_VERSION_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.SOCIAL_PUBLISH_VERSION_ERROR,
				data: { versions: versionIds.map(id => ({ id })) },
			},
		});
	};
}

export function socialUnpublishVersion(versionIds) {
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.SOCIAL_UNPUBLISH_VERSION_AJAX,
			payload: { isPublish: false },
			AJAX: {
				url: "ui/versions/social/unpublish",
				method: "post",
				success: CREATIVE_VERSIONS_ACTIONS.SOCIAL_UNPUBLISH_VERSION_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.SOCIAL_UNPUBLISH_VERSION_ERROR,
				data: { versions: versionIds.map(id => ({ id })) },
			},
		});
	};
}


export function importDecisionSheet(creativeId, data) {
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.IMPORT_DECISION_SHEET_AJAX,
			AJAX: {
				url: `creatives/${creativeId}/hedge-upload`,
				method: "post",
				data,
				gateway: "dt",
				headers: { "Content-Type": "multipart/form-data" },
				version: 0,
				success: CREATIVE_VERSIONS_ACTIONS.IMPORT_DECISION_SHEET_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.IMPORT_DECISION_SHEET_ERROR,
			},
		});
	};
}

export function importVersions(data, withPreview = false) {
	const params = withPreview ? { dirty: true } : {};
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.IMPORT_VERSIONS_AJAX,
			AJAX: {
				url: `ui/version/import_temp`,
				method: "post",
				data,
				params,
				headers: { "Content-Type": "multipart/form-data" },
				success: CREATIVE_VERSIONS_ACTIONS.IMPORT_VERSIONS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.IMPORT_VERSIONS_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		});
	};
}

export function importVersionLabels(data, libraryId, creativeId) {
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.IMPORT_VERSIONS_LABELS_AJAX,
			AJAX: {
				method: "post",
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/versions/labels/import/async`,
				data,
				headers: { "Content-Type": "multipart/form-data" },
				success: CREATIVE_VERSIONS_ACTIONS.IMPORT_VERSIONS_LABELS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.IMPORT_VERSIONS_LABELS_ERROR,
			},
		});
	};
}

function generateVersionsBuildMessage(id, versionNamePrefix){
	return function buildMessage(response, success){
		if(success){
			const { numberOfVersionsGenerated } = response.data || {};
			return {
				type: "success",
				id,
				messageHeader: "Version generation complete",
				message: `Successfully generated ${numberOfVersionsGenerated} “${versionNamePrefix}” versions`,
			};
		}

		return {
			type: "error",
			id,
			messageHeader: "Error!",
			message: `Error generating "${versionNamePrefix}" versions`,
		};
	};
}

export function fetchVersionsWithPrefix(libraryId, creativeIds, versionNamePrefix){
	return (dispatch) => {
		if(!versionNamePrefix){
			return Promise.reject({
				success: false,
				versionNamePrefix: "Version Name Prefix is required",
			});
		}
		const url = `ui/creative-libraries/${libraryId}/versions?advancedFilter=all(in(creativeId,${creativeIds.join(',')}),like(name,${encodeURIComponent(versionNamePrefix)}%20*))&rpp=1`;

		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.FETCH_VERSIONS_WITH_PREFIX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url, /*
					We would normally use a `params` property here, instead of tacking onto the url,
					but we need to manually URL encode a backslash (%5c) to escape the underscore character,
					so that it's read as part of the `like` search string, instead of as a single character wildcard.

					If we put a backslash into a `params` value, it doesn't get URL encoded, and we get a 400 bad request error,
					and if we manually escape it with "%5c", the `%` gets URL encoded, and we'd get "%255c" in the request,
					which doesn't work.

					Note that the user-provided `versionNamePrefix` is manually URL encoded for safety, to compensate for what
					the `params` property usually does.
				*/
				success: CREATIVE_VERSIONS_ACTIONS.FETCH_VERSIONS_WITH_PREFIX_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.FETCH_VERSIONS_WITH_PREFIX_ERROR,
			},
		}).then((response) => {
			if(response?.success){
				return response.payload?.data?.items;
			}
		});
	};
}

export function generateVersions(libraryId, creativeId, data, options = {}){
	/*
		data is expected to be an object formatted like:
		{
			isAutoOptimization: Boolean if auto-opt checkbox is checked or not
			versionNamePrefix: "Summer_sales",
			variables: [
				{
					name: "Headline_text",
					type: "Text",
					values: [
						// String values for Text
						"Summer sales Event",
						"Unbeatable Prices"
					]
				},
				{
					name: "Background_image",
					type: "Image",
					values: [
						// ID values for videos / images / richloads
						12345,
						67890
					]
				},
			]
		}
	*/
	const { versionNamePrefix } = data;
	const url = `creative-libraries/${libraryId}/creatives/${creativeId}/versions/generate`;

	if(data.isAutoOptimization){ data.autoOptimizationGoal = "modern"; }

	const meta = options.suppressGrowl ? SUPPRESS_ALL_GROWL : {
		buildMessage: generateVersionsBuildMessage(url, versionNamePrefix),
	};

	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.GENERATE_VERSIONS_AJAX,
			AJAX: {
				url,
				method: "post",
				data,
				success: CREATIVE_VERSIONS_ACTIONS.GENERATE_VERSIONS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.GENERATE_VERSIONS_ERROR,
			},
			meta,
		});
	};
}

export function resetGeneration(){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.RESET_GENERATION,
		});
	};
}

export function fetchAutoAssignVariableAssets(libraryId, creativeId, contentTagId){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.FETCH_AUTO_ASSIGN_VARIABLE_ASSETS,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/creatives/${creativeId}/content-tag-auto-assign`,
				method: "post",
				data: { id: contentTagId },
				success: CREATIVE_VERSIONS_ACTIONS.FETCH_AUTO_ASSIGN_VARIABLE_ASSETS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.FETCH_AUTO_ASSIGN_VARIABLE_ASSETS_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		}).then((response) => {
			if(response?.success){
				return response?.payload?.data;
			}
			return Promise.reject();
		});
	};
}

export function fetchImportPreviewVersions(jobId, creativeId){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.FETCH_IMPORT_PREVIEW_VERSIONS,
			AJAX: {
				url: `ui/version/import/${jobId}/creatives/${creativeId}/versions`,
				method: "get",
				success: CREATIVE_VERSIONS_ACTIONS.FETCH_IMPORT_PREVIEW_VERSIONS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.FETCH_IMPORT_PREVIEW_VERSIONS_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		});
	};
}

export function confirmImport(jobId){
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.CONFIRM_VERSION_IMPORT,
			AJAX: {
				url: `ui/version/import/${jobId}/confirm`,
				method: "post",
				success: CREATIVE_VERSIONS_ACTIONS.CONFIRM_VERSION_IMPORT_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.CONFIRM_VERSION_IMPORT_ERROR,
			},
		});
	};
}

export function sendForAutoClassification(creativeId, versionIds = []){
	const versions = versionIds.map((id) => {
		return { id };
	});

	return (dispatch) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.CLASSIFY_VERSIONS_AJAX,
			AJAX: {
				url: `ui/creatives/${creativeId}/versions/classification`,
				method: "post",
				data: {
					versions,
				},
				success: CREATIVE_VERSIONS_ACTIONS.CLASSIFY_VERSIONS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.CLASSIFY_VERSIONS_ERROR,
			},
			meta: {
				buildMessage(response, success){
					if(success){
						return {
							type: "success",
							id: new Date().getTime(),
							messageHeader: "Success",
							message: "Versions sent for auto-classification",
						};
					} else {
						return {
							type: "error",
							id: new Date().getTime(),
							messageHeader: "Error",
							message: "Error sending Versions for auto-classification",
						};
					}
				},
			},
		});
	};
}

const initialCreativeVersionsState = {
	success: true,
	items: [],

	totalPages: 1,
	totalItems: 0,

	isLoaded: false,
	hasErrorLoading: false,

	multiVariant: {
		isSaving: false,
		hasErrorSaving: false,
	},

	generation: {
		isSaving: false,
		hasErrorSaving: false,
		errors: [],
	},

	classification: {
		isSaving: false,
	},
};

const actionCreators = {
	clearCreativeVersions,
	loadCreativeVersions,
	fetchAllVersions,
	fetchSingleVersion,
	deleteVersions,
	createVersion,
	updateVersion,
	duplicateVersion,
	loadCreativeVersionsDistincts,
	createAutoOptimizedCreative,
	createWeightedRotationCreative,
	publishVersion,
	socialPublishVersion,
	socialUnpublishVersion,
	fetchNamedVersion,
	importDecisionSheet,
	importVersions,
	importVersionLabels,

	generateVersions,
	resetGeneration,
	fetchImportPreviewVersions,
	confirmImport,
};

const handlers = {
	[CREATIVE_VERSIONS_ACTIONS.RESET_CREATIVE_VERSIONS_LIST]: clearList,
	[CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_AJAX]: isLoadingCreativeVersions,
	[CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_SUCCESS]: initCreativeVersions,
	[CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_ERROR]: initErrorCreativeVersions,

	[CREATIVE_VERSIONS_ACTIONS.DELETE_VERSIONS_AJAX]: setDeleting,
	[CREATIVE_VERSIONS_ACTIONS.DELETE_VERSIONS_SUCCESS]: deletingSuccess,
	[CREATIVE_VERSIONS_ACTIONS.DELETE_VERSIONS_ERROR]: deletingError,

	[CREATIVE_VERSIONS_ACTIONS.CREATE_CREATIVE_VERSION_AJAX]: initCreateVersion,
	[CREATIVE_VERSIONS_ACTIONS.CREATE_CREATIVE_VERSION_SUCCESS]: createVersionSuccess,
	[CREATIVE_VERSIONS_ACTIONS.CREATE_CREATIVE_VERSION_ERROR]: createVersionError,

	[CREATIVE_VERSIONS_ACTIONS.UPDATE_VERSION_SUCCESS]: updateVersionSuccess,

	[CREATIVE_VERSIONS_ACTIONS.CREATE_AUTO_OPTIMIZED_CREATIVE_AJAX]: isCreatingMultiVariantCreative,
	[CREATIVE_VERSIONS_ACTIONS.CREATE_AUTO_OPTIMIZED_CREATIVE_SUCCESS]: initMultiVariantCreative,
	[CREATIVE_VERSIONS_ACTIONS.CREATE_AUTO_OPTIMIZED_CREATIVE_ERROR]: errorMultiVariantCreative,

	[CREATIVE_VERSIONS_ACTIONS.CREATE_WEIGHTED_ROTATION_CREATIVE_AJAX]: isCreatingMultiVariantCreative,
	[CREATIVE_VERSIONS_ACTIONS.CREATE_WEIGHTED_ROTATION_CREATIVE_SUCCESS]: initMultiVariantCreative,
	[CREATIVE_VERSIONS_ACTIONS.CREATE_WEIGHTED_ROTATION_CREATIVE_ERROR]: errorMultiVariantCreative,

	[CREATIVE_VERSIONS_ACTIONS.PUBLISH_VERSION_AJAX]: isPublishingVersion,
	[CREATIVE_VERSIONS_ACTIONS.PUBLISH_VERSION_SUCCESS]: publishVersionSuccess,
	[CREATIVE_VERSIONS_ACTIONS.PUBLISH_VERSION_ERROR]: publishVersionError,

	[CREATIVE_VERSIONS_ACTIONS.SOCIAL_PUBLISH_VERSION_AJAX]: isPublishingVersion,
	[CREATIVE_VERSIONS_ACTIONS.SOCIAL_PUBLISH_VERSION_SUCCESS]: socialPublishSuccess,
	[CREATIVE_VERSIONS_ACTIONS.SOCIAL_PUBLISH_VERSION_ERROR]: publishVersionError,

	// Unpublish actions do the same thing as published, just need loading state set
	[CREATIVE_VERSIONS_ACTIONS.SOCIAL_UNPUBLISH_VERSION_AJAX]: isPublishingVersion,
	[CREATIVE_VERSIONS_ACTIONS.SOCIAL_UNPUBLISH_VERSION_SUCCESS]: socialPublishSuccess,
	[CREATIVE_VERSIONS_ACTIONS.SOCIAL_UNPUBLISH_VERSION_ERROR]: publishVersionError,


	[CREATIVE_VERSIONS_ACTIONS.GENERATE_VERSIONS_AJAX]: setGenerating,
	[CREATIVE_VERSIONS_ACTIONS.GENERATE_VERSIONS_SUCCESS]: generatingSuccess,
	[CREATIVE_VERSIONS_ACTIONS.GENERATE_VERSIONS_ERROR]: generatingError,

	[CREATIVE_VERSIONS_ACTIONS.RESET_GENERATION]: reinitializeGeneration,

	[CREATIVE_VERSIONS_ACTIONS.CLASSIFY_VERSIONS_AJAX]: setClassifying,
	[CREATIVE_VERSIONS_ACTIONS.CLASSIFY_VERSIONS_SUCCESS]: classifyingSuccess,
	[CREATIVE_VERSIONS_ACTIONS.CLASSIFY_VERSIONS_ERROR]: classifyingError,

	[LABELS_ACTIONS.ADD_VERSION_LABELS_SUCCESS]: updateLabelCount,
	[LABELS_ACTIONS.DELETE_VERSION_LABELS_SUCCESS]: subtractLabels,
};

let reducerConfig = {
	typeRoot: "CREATIVE_VERSIONS",
	initialState: initialCreativeVersionsState,
	actionTypes: CREATIVE_VERSIONS_ACTIONS,
	actionCreators,
	handlers,
};

reducerConfig = addFiltering(reducerConfig, { sort: "lastUpdateDate:DESC" });
reducerConfig = addColumnFilters(reducerConfig, {
	status: {
		name: "status",
		type: "Checkbox",
		displayName: "Status",
		dictionaryName: "versionStatus",
	},
	weightMultiplier: {
		name: "weightMultiplier",
		type: "Checkbox",
		displayName: "Weight",
	},
	filesize: {
		name: "filesize",
		type: "Range",
		displayName: "File Size",
	},
	labelsCnt:{
		name:"labelsCnt",
		type:"Range",
		displayName:"Creative Labels",
	},
	autoClassificationStatus: {
		name: "autoClassificationStatus",
		type: "Checkbox",
		displayName: "Auto-classification Status",
	},
	lastUpdateDate: {
		name: "lastUpdateDate",
		type: "Date",
		displayName: "Last Modified",
	},
});

const { updateColumnFilter } = reducerConfig.actionCreators;

function loadCreativeVersionsDistincts(libraryId, creativeId, columnNameList){
	let commaSeparatedColumnNames = columnNameList.join(",");

	return (dispatch, getState) => {
		return dispatch({
			type: CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_DISTINCTS_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}/versions/distincts`,
				params: { distincts: commaSeparatedColumnNames },
				success: CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_DISTINCTS_SUCCESS,
				error: CREATIVE_VERSIONS_ACTIONS.LOAD_CREATIVE_VERSIONS_DISTINCTS_ERROR,
			},
		}).then((response) => {
			let { data = {} } = response?.payload || {};
			let keys = Object.keys(data);

			keys.forEach((key) => {
				let distincts = data[key];
				dispatch(updateColumnFilter(key, {
					options: distincts,
				}));
			});
		});
	};
}

function clearList(state, action){
	return {
		...initialCreativeVersionsState,
		filterInfo: state.filterInfo,
		columnFilters: state.columnFilters,
	};
}

function isLoadingCreativeVersions(state, action){
	return {
		...state,
		isLoaded: false,
	};
}

function initCreativeVersions(state, action){
	let { data } = action.payload;
	return {
		...state,
		...data,
		isLoaded: true,
		hasErrorLoading: false,
	};
}

function initErrorCreativeVersions(state, action){
	return {
		...state,
		isLoaded: true,
		hasErrorLoading: true,
	};
}

function setDeleting(state, action) {
	return {
		...state,
		isLoaded: false,
	};
}

function deletingSuccess(state, action) {
	const deleted = new Set(action.AJAX.data.versionIds);
	const items = state.items.filter(item => !deleted.has(item.id));
	const newTotalItems = state.totalItems - (state.items.length - items.length);

	let { totalPages } = state
		, selectedPage = state.filterInfo.page;

	/*
		Adjust the current page and totalPages if we need to decrease the pages after deletion
		We can calculate the maxTotalPages by dividing the totalItems after deletion
		by the rpp, and then rounding the number up.
	*/
	const maxTotalPages = Math.ceil(newTotalItems / state.filterInfo.rpp);

	if (maxTotalPages < state.totalPages) {
		totalPages = maxTotalPages;
		if (maxTotalPages < state.filterInfo.page) {
			selectedPage = maxTotalPages;
		}
	}

	return {
		...state,
		filterInfo: {
			...state.filterInfo,
			page: selectedPage,
		},
		isLoaded: true,
		totalItems: newTotalItems,
		totalPages,
		items,
	};
}

function deletingError(state, action) {
	return {
		...state,
		isLoaded: true,
		hasErrorLoading: true,
	};
}

function initCreateVersion(state, action){
	return{
		...state,
	};
}

function createVersionSuccess(state, action){
	const { data } = action.payload
		, { items } = state
		, newItems = [data, ...items]
		, newTotalItems = state.totalItems + 1;

	let { totalPages } = state;
	// Adjust the totalPages if we need to add any pages after addition
	const maxTotalPages = Math.ceil(newTotalItems / state.filterInfo.rpp);
	if (maxTotalPages > state.totalPages) {
		totalPages = maxTotalPages;
	}

	return {
		...state,
		totalPages,
		totalItems: newTotalItems,
		items: newItems,
		isLoaded: true,
		hasErrorLoading: false,
	};
}

function createVersionError(state, action){
	return {
		...state,
		isLoaded: true,
		hasErrorLoading: true,
	};
}

export function updateVersionSuccess(state, action){
	let updatedVersion = action?.payload?.data;
	let newItems = state.items.map((version) => {
		let { id } = version;
		if(id === updatedVersion.id){
			return updatedVersion;
		}
		return version;
	});

	return {
		...state,
		items: newItems,
	};
}

function isPublishingVersion(state, action){
	return {
		...state,
		isLoaded: false,
	};
}

function socialPublishSuccess(state, action) {
	const { data, original } = action.payload;
	const { versionIds = []} = data;
	const { isPublish } = original;
	const { items } = state;

	const updatedItems = items.map((item) => {
		if (versionIds.includes(item.id)) {
			return { ...item, status: isPublish ? VERSION_STATUS.LIVE : VERSION_STATUS.UNPUBLISHED };
		}

		return item;
	});

	return {
		...state,
		items: updatedItems,
		isLoaded: true,
	};
}

function publishVersionSuccess(state, action){
	return {
		...state,
		isLoaded: true,
	};
}

function publishVersionError(state, action){
	return {
		...state,
		isLoaded: true,
	};
}

/*
	Multi Variant Reducers
*/
export function isCreatingMultiVariantCreative(state, action){
	return {
		...state,
		multiVariant: {
			isSaving: true,
			hasErrorSaving: false,
		},
	};
}

export function initMultiVariantCreative(state, action){
	return {
		...state,
		multiVariant: {
			isSaving: false,
			hasErrorSaving: false,
		},
	};
}

export function errorMultiVariantCreative(state, action){
	return {
		...state,
		multiVariant: {
			isSaving: false,
			hasErrorSaving: true,
		},
	};
}

/*
	Generate Versions Reducers
*/
function reinitializeGeneration(state){
	return {
		...state,
		generation: {
			...initialCreativeVersionsState.generation,
		},
	};
}

function setGenerating(state, action){
	return {
		...state,
		generation: {
			isSaving: true,
			hasErrorSaving: false,
			errors: [],
		},
	};
}

function generatingSuccess(state, action){
	return {
		...state,
		generation: {
			isSaving: false,
			hasErrorSaving: false,
			errors: [],
		},
	};
}

function generatingError(state, action){
	const errors = action?.payload?.errorObj?.data || [];
	return {
		...state,
		generation: {
			isSaving: false,
			hasErrorSaving: true,
			errors,
		},
	};
}

/*
	Classify Versions Reducers
*/
function setClassifying(state, action){
	return {
		...state,
		classification: {
			isSaving: true,
		},
	};
}

function classifyingSuccess(state, action){
	const classifiedVersions = action.AJAX?.data?.versions || [];

	return {
		...state,
		items: state.items.map((item) => {
			if (classifiedVersions.find(version => Number(version.id) === item.id)) {
				return { ...item, autoClassificationStatus: "In progress" };
			}

			return item;
		}),
		classification: {
			isSaving: false,
		},
	};
}

function classifyingError(state, action){
	return {
		...state,
		classification: {
			isSaving: false,
		},
	};
}

function updateLabelCount(state, action){
	const payloadVersions = action.payload?.data?.versions || [];
	const items = payloadVersions.reduce((prev, version) => {
		let existingVersion = prev.find(item => `${item.id}` === `${version.id}`);
		if(existingVersion){
			existingVersion.labelsCnt = version.labels.length;
		}
		return prev;
	}, [...state.items]);

	const newState = {
		...state,
		items,
	};

	return newState;
}

function subtractLabels(state, action){
	const deletedIds = action.payload?.data?.deleted || [];
	const { versionId } = action.payload.original || {};

	return {
		...state,
		items: state.items.map((version) => {
			if(`${version.id}` === `${versionId}`){
				version.labelsCnt = version.labelsCnt - deletedIds.length;
			}
			return version;
		}),
	};
}

export const PRIVATE = { socialPublishSuccess };

export default createReducer(reducerConfig.initialState, reducerConfig.handlers);
export const CreativeVersions = reducerConfig.actionCreators;
